<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import FlowsAPI from 'dashboard/api/flows';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const flows = ref([]);
const isFetching = ref(false);
const deletingId = ref(null);
const selectedFlow = ref(null);
const showDeletePopup = ref(false);

const hasFlows = computed(() => flows.value.length > 0);

const fetchFlows = async () => {
  isFetching.value = true;
  try {
    const { data } = await FlowsAPI.getFlows();
    flows.value = Array.isArray(data) ? data : [];
  } catch {
    useAlert(t('FLOWS.LIST.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const openBuilder = flowId => {
  router.push(accountScopedRoute('flow_builder', { flowId }));
};

const openNewFlow = () => openBuilder('new');

const openDeletePopup = flow => {
  selectedFlow.value = flow;
  showDeletePopup.value = true;
};

const closeDeletePopup = () => {
  showDeletePopup.value = false;
  selectedFlow.value = null;
};

const confirmDeletion = async () => {
  const flow = selectedFlow.value;
  if (!flow) return;

  deletingId.value = flow.id;
  closeDeletePopup();
  try {
    await FlowsAPI.deleteFlow(flow.id);
    flows.value = flows.value.filter(item => item.id !== flow.id);
  } catch {
    useAlert(t('FLOWS.LIST.DELETE_ERROR'));
  } finally {
    deletingId.value = null;
  }
};

const statusLabel = flow => {
  if (flow.status === 'published') return t('FLOWS.LIST.PUBLISHED');
  if (flow.status === 'paused') return t('FLOWS.LIST.PAUSED');

  return t('FLOWS.LIST.DRAFT');
};

const statusClasses = flow => {
  if (flow.status === 'published') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (flow.status === 'paused') {
    return 'bg-slate-100 text-slate-700 border-slate-200';
  }

  return 'bg-amber-50 text-amber-700 border-amber-200';
};

const triggerSummary = flow => {
  const trigger = flow.trigger_config || {};
  if (trigger.type === 'stage_entry') {
    return t('FLOWS.LIST.TRIGGER_STAGE_ENTRY', { stage: trigger.stage || '-' });
  }

  return t('FLOWS.LIST.TRIGGER_NO_REPLY', {
    minutes: trigger.delay_minutes || 60,
  });
};

const formattedDate = value => {
  if (!value) return '-';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

onMounted(fetchFlows);
</script>

<template>
  <section class="flex flex-col flex-1 min-h-0 overflow-y-auto bg-n-background">
    <header class="px-6 pt-6 pb-5 border-b border-n-weak">
      <div class="flex flex-wrap items-start gap-4">
        <div class="flex-1 min-w-0">
          <p
            class="mb-1 text-xs font-medium uppercase tracking-[0.14em] text-n-slate-10"
          >
            {{ t('FLOWS.LIST.EYEBROW') }}
          </p>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ t('FLOWS.LIST.TITLE') }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('FLOWS.LIST.DESCRIPTION') }}
          </p>
        </div>
        <Button icon="i-lucide-plus" @click="openNewFlow">
          {{ t('FLOWS.LIST.NEW') }}
        </Button>
      </div>
    </header>

    <div v-if="isFetching" class="flex items-center justify-center flex-1">
      <Spinner class="text-n-brand" />
    </div>

    <div
      v-else-if="!hasFlows"
      class="flex flex-col items-center justify-center flex-1 gap-3 px-6 text-center"
    >
      <span class="i-lucide-workflow text-5xl text-n-slate-8" />
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ t('FLOWS.LIST.EMPTY_TITLE') }}
      </h2>
      <p class="max-w-md text-sm text-n-slate-11">
        {{ t('FLOWS.LIST.EMPTY_DESCRIPTION') }}
      </p>
      <Button class="mt-2" icon="i-lucide-plus" @click="openNewFlow">
        {{ t('FLOWS.LIST.EMPTY_ACTION') }}
      </Button>
    </div>

    <div v-else class="grid gap-4 p-6 md:grid-cols-2 xl:grid-cols-3">
      <article
        v-for="flow in flows"
        :key="flow.id"
        class="flex flex-col gap-4 p-5 transition-shadow border rounded-2xl border-n-weak bg-n-solid-1 hover:shadow-md"
      >
        <div class="flex items-start gap-3">
          <span
            class="flex items-center justify-center w-10 h-10 rounded-xl bg-n-brand/10 text-n-brand"
          >
            <span class="i-lucide-git-branch text-lg" />
          </span>
          <div class="flex-1 min-w-0">
            <h2 class="text-base font-semibold truncate text-n-slate-12">
              {{ flow.name }}
            </h2>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ triggerSummary(flow) }}
            </p>
          </div>
          <span
            class="px-2 py-1 text-[0.6875rem] font-medium border rounded-full"
            :class="statusClasses(flow)"
          >
            {{ statusLabel(flow) }}
          </span>
        </div>

        <div class="grid grid-cols-2 gap-3 text-xs text-n-slate-11">
          <div class="p-3 rounded-xl bg-n-alpha-black1">
            <p class="mb-1 text-n-slate-10">
              {{ t('FLOWS.LIST.NODES') }}
            </p>
            <p class="text-lg font-semibold text-n-slate-12">
              {{ flow.graph?.nodes?.length || 0 }}
            </p>
          </div>
          <div class="p-3 rounded-xl bg-n-alpha-black1">
            <p class="mb-1 text-n-slate-10">
              {{ t('FLOWS.LIST.CONNECTIONS') }}
            </p>
            <p class="text-lg font-semibold text-n-slate-12">
              {{ flow.graph?.edges?.length || 0 }}
            </p>
          </div>
        </div>

        <p class="text-[0.6875rem] text-n-slate-10">
          {{
            t('FLOWS.LIST.UPDATED_AT', { date: formattedDate(flow.updated_at) })
          }}
        </p>

        <div class="flex items-center gap-2 pt-1">
          <Button size="sm" class="flex-1" @click="openBuilder(flow.id)">
            {{ t('FLOWS.LIST.EDIT') }}
          </Button>
          <Button
            size="sm"
            color="slate"
            :is-loading="deletingId === flow.id"
            @click="openDeletePopup(flow)"
          >
            {{ t('FLOWS.LIST.DELETE') }}
          </Button>
        </div>
      </article>
    </div>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="t('FLOWS.LIST.DELETE_TITLE')"
      :message="t('FLOWS.LIST.DELETE_CONFIRM')"
      :message-value="selectedFlow ? ` ${selectedFlow.name}?` : ''"
      :confirm-text="t('FLOWS.LIST.DELETE_YES')"
      :reject-text="t('FLOWS.LIST.DELETE_NO')"
    />
  </section>
</template>
